import { Autocomplete, Chip, TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Radio,
  RadioGroup,
  FormControlLabel,
  Pagination,
  useMediaQuery,
  InputAdornment,
  Typography,
 } from "@mui/material";
import React, { useEffect, useState, useMemo } from "react";
import { axiosRequest } from "../../service/AxiosService";
import { useNavigate } from "react-router-dom";
import SearchIcon from '@mui/icons-material/Search';
import { useUser } from "../../context/UserContext";
// 検索ダイアログ用
import { styled } from "@mui/material/styles";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import { useTheme } from "@mui/material/styles";
import { ApplicationEntriesType } from "./data/ApplicationEntriesType";
import { fetchApplicationEntryList } from "../../service/CustomFormService";
import {
  ExpandMore as ChevronDown,
  ExpandLess as ChevronUp,
  Photo as PhotoIcon,
} from "@mui/icons-material";
import ClearSharpIcon from "@mui/icons-material/ClearSharp";
import dayjs from "dayjs";

type Props = {
    value: string;
    onChange: (val: string) => void;
    required: boolean;
    error: boolean;
    helperText?: string;
};

/**
 * 申請番号入力フィールドコンポーネント
 * - 申請番号をChipとして複数入力でき、入力時に申請番号の存在チェックを行う
 * - 検索アイコンクリックで申請一覧ダイアログを表示し、申請番号を選択できる
 * - props: value (カンマ区切りの申請番号文字列), onChange (変更時コールバック),
 *          required (必須フラグ), error (エラー状態), helperText (エラーメッセージ)
 */
const ApplicationNumberField = ({ value, onChange, required, error, helperText}: Props) => {
    const [numbers, setNumbers] = useState<string[]>(value ? value.split(",") : []);
    const [inputValue, setInputValue] = useState("");
    // 検索ダイアログ用
    const [openDialog, setOpenDialog] = useState(false);
    const { user } = useUser(); // useUser フックを使ってユーザー情報を取得

    useEffect(() => {
        onChange(numbers.join(","));
    }, [numbers]);

    // 申請番号をクリックした際に申請詳細画面を新しいタブで開く
    const handleClick = async (applicationNumber: string) => {
        const response = await axiosRequest<string>(
            "/applicationGetId",
            "GET",
            { params: { applicationNumber } }
        );

        const applicationId = response;
        if (applicationId) {
            window.open(`/detail/${applicationId}`, "_blank");
        } else {
            alert("該当申請が見つかりません");
        }
    };

    // 申請番号存在チェック
    const validateOption = async (number: string) => {
        const response = await axiosRequest<string>(
            "/customForm/existsNumber",
            "GET",
            { params: { number } }
        );
        return response;
    };
    // 申請番号の入力完了時（Chipの×ボタン押下時も実行）
    // 設定済み項目の存在チェックを実行。存在しないものは除外する
    const handleChange = async (event: any, newValue: string[]) => {
        const validated = [];

        for (const option of newValue) {
          const exists = await validateOption(option);
          if (exists) {
            validated.push(option);
          }
        }

        setNumbers(validated);
    };

    // 入力値を数値のみに制限して状態を更新する
    const handleInputChange = (event: any, newInputValue: string) => {
        // 数値のみ許可（空文字はOK）
        if (newInputValue === "" || /^[0-9]+$/.test(newInputValue)) {
            setInputValue(newInputValue);
        }
    };

    // ダイアログ制御用関数
    const handleOpenDialog = () => setOpenDialog(true);
    const handleCloseDialog = () => {
        setOpenDialog(false);
    };
    // ダイアログで行選択した時の処理
    const setSelectDialogValue = async (val: string) => {
        setInputValue(val);
        const trimmed = String(val).trim();
        await callValidate(trimmed);
    };

    // 申請番号入力時の処理
    const handleClose = async () => {
        const trimmed = inputValue.trim();
        await callValidate(trimmed);
    };

    // 申請番号を検索して、存在すればnumbersに設定する。
    const callValidate = async (val : string) => {
        if (val && !numbers.includes(val) ) {
            const exists = await validateOption(val);
            if (exists) {
                setNumbers([...numbers, val]);
                setInputValue("");
            } else {
                setInputValue("");
            }
        } else {
            setInputValue("");
        }
    };

// #region ダイアログに表示する内容
// テーブルのスタイルを設定
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.custom.gray,
    color: theme.palette.common.white,
    whiteSpace: "nowrap", // 改行しない
    padding: "10px 20px",
  },
  [`&.${tableCellClasses.body}`]: {
    whiteSpace: "nowrap",
    padding: "6px 20px",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

/**
 * HTMLをパースしてReact要素に変換する
 * @param html - パースするHTMLコンテンツ
 * @returns React要素の配列
 */
const parseRichTextHTML = (html: string): React.ReactNode[] => {
  if (!html) return [];

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const elements: React.ReactNode[] = [];
  let key = 0;

  const processNode = (node: ChildNode) => {
    if (node.nodeType === Node.TEXT_NODE) {
      // テキストノードはトリムせずにそのまま保持（改行と空白を維持）
      const text = node.textContent;
      if (text) {
        elements.push(<span key={key++}>{text}</span>);
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element;

      if (element.tagName === 'IMG') {
        // 画像タグはPhotoIconに置き換え
        elements.push(
          <PhotoIcon
            key={key++}
            sx={{ fontSize: 16, verticalAlign: 'middle', color: '#1976d2', mx: 0.5 }}
          />
        );
      } else if (element.tagName === 'BR') {
        // 改行タグ
        elements.push(<br key={key++} />);
      } else if (element.tagName === 'P') {
        // 段落タグ: div で囲んで子要素を処理
        const pElements: React.ReactNode[] = [];
        let pKey = 0;
        element.childNodes.forEach(child => {
          if (child.nodeType === Node.TEXT_NODE) {
            const text = child.textContent;
            if (text) {
              pElements.push(<span key={pKey++}>{text}</span>);
            }
          } else if (child.nodeType === Node.ELEMENT_NODE) {
            const childElement = child as Element;
            if (childElement.tagName === 'IMG') {
              pElements.push(
                <PhotoIcon
                  key={pKey++}
                  sx={{ fontSize: 16, verticalAlign: 'middle', color: '#1976d2', mx: 0.5 }}
                />
              );
            } else if (childElement.tagName === 'BR') {
              pElements.push(<br key={pKey++} />);
            }
          }
        });
        elements.push(<div key={key++}>{pElements}</div>);
      } else {
        // その他のタグ: 子要素を再帰的に処理
        element.childNodes.forEach(child => processNode(child));
      }
    }
  };

  doc.body.childNodes.forEach(node => processNode(node));

  return elements;
};

const columnLabels: Partial<Record<keyof ApplicationEntriesType, string>> = {
  number: "番号",
  submittedAt: "申請日",
  type: "申請種別",
  applicationName: "申請書名",
  applicant: "申請者",
  userProfiles: "申請者情報",
  status: "申請ステータス",
  currentActivity: "現在のアクティビティ",
  formSummary: "申請内容",
  reportFileStatus: "添付ファイル",
};

/**
 * 申請一覧コンポーネント（ダイアログ内表示用）
 * - 申請一覧を検索・ソート・ページング機能付きで表示する
 * - 行選択時に申請番号を親コンポーネントに通知してダイアログを閉じる
 */
const ApplicationList: React.FC = () => {
  const isMobile = useMediaQuery("(max-width:600px)"); // 600px 以下でモバイル表示
  const theme = useTheme();
  const [data, setData] = useState<ApplicationEntriesType[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(() => {
    return sessionStorage.getItem("searchText_ApplicationAudit") || ""; // sessionStorageに値がある場合はその値を格納。ない場合は空文字を格納。
  });
  // const [sortAsc, setSortAsc] = useState(true);
  const [sortConfig, setSortConfig] = useState<{
    key: keyof ApplicationEntriesType;
    asc: boolean;
  }>({
    key: "number",
    asc: false,
  });
  const [hoveredColumn, setHoveredColumn] = useState<string | null>(null);
  // sessionStorageに値がある場合はその値を格納。ない場合はデフォルト値を格納。
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => {
    const stored = sessionStorage.getItem("visibleColomns_ApplicationAudit");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          return new Set<string>(parsed);
        }
      } catch (e) {
        console.warn("visibleColumns の復元に失敗しました:", e);
      }
    }
    return new Set<string>(Object.keys(columnLabels));
  });
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  // 表示件数のデフォルトと選択できる件数を設定。
  const defaultRowPerPage = 50;
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    return (
      Number(sessionStorage.getItem("showItems_ApplicationAudit")) ||
      defaultRowPerPage
    ); // sessionStorageに値がある場合はその値を格納。ない場合はデフォルト値を格納。
  });
  const rowPerPageList = [50, 100, 1000];
  const [page, setPage] = useState(() => {
    // sessionStorageに値がある場合はその値を格納。それ以外は1をセットする。
    const stored = sessionStorage.getItem("paging_ApplicationAudit");
    const parsed = stored ? parseInt(stored, 10) : NaN;
    return !isNaN(parsed) && parsed > 0 ? parsed : 1;
  });
  const statusMap: Record<string, string> = {
    NotStarted: "申請前",
    InProgress: "申請中",
    Completed: "完了",
    Canceled: "取り下げ",
    Failed: "却下",
    Error: "エラー終了",
    SendBack: "差し戻し",
    Draft: "一時保存",
  };

  // ステータスフィルターのラベル定義
  const statusFilterLabels = [
    { key: "Completed", label: "完了" },
    { key: "InProgress", label: "申請中" },
    { key: "SendBack", label: "差し戻し" },
    { key: "Canceled", label: "取り下げ" },
    { key: "Failed", label: "却下" },
    { key: "Draft", label: "一時保存" },
    { key: "all", label: "すべて" },
  ];

  const [statusFilter, setStatusFilter] = useState("Completed");

  const navigate = useNavigate();

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const result = await fetchApplicationEntryList();
      setData(result);
      setLoading(false);
    };
    loadData();
  }, []);

  // 指定カラムの表示・非表示を切り替えてsessionStorageに保存する
  const toggleColumn = (key: string) => {
    const newSet = new Set(visibleColumns);
    newSet.has(key) ? newSet.delete(key) : newSet.add(key);
    sessionStorage.setItem(
      "visibleColomns_ApplicationAudit",
      JSON.stringify(Array.from(newSet))
    ); // 表示するカラム情報をsessionStorageに格納する。
    setVisibleColumns(newSet);
  };

  // 指定行の展開・折りたたみ状態を切り替える
  const toggleExpand = (id: string) => {
    const newSet = new Set(expandedRows);
    newSet.has(id) ? newSet.delete(id) : newSet.add(id);
    setExpandedRows(newSet);
  };

  const filteredData = useMemo(() => {
    const keywords = searchTerm.toLowerCase().split(/\s+/).filter(Boolean);
    // 検索対象外のフィールド（非表示フィールド）
    const excludeFields = ["applicationEntryId", "richTextMap"];

    let result = data.filter((row) =>
      keywords.every((keyword) =>
        Object.entries(row).some(([key, value]) => {
          let displayValue = "";

          // 非表示フィールドはスキップ
          if (excludeFields.includes(key)) {
            return false;
          }

          // statusを日本語で検索できる
          if (key === "status" && typeof value === "string") {
            displayValue = statusMap[value] || "";
          } else if (typeof value === "string") {
            displayValue = value;
          } else if (key === "submittedAt") {
            displayValue = dayjs(value).isValid() ? dayjs(value).format("YYYY/MM/DD") : "未提出";
          } else if (typeof value === "number") {
            // 数値型を文字列に変換
            displayValue = String(value);
          } else if (value !== null && value !== undefined) {
            // その他の型も文字列に変換（オブジェクトは除く）
            if (typeof value !== "object") {
              displayValue = String(value);
            }
          }

          return displayValue.toLowerCase().includes(keyword);
        })
      )
    );

    if (sortConfig) {
      const { key, asc } = sortConfig;
      result = [...result].sort((a, b) => {
        const aValue = a[key];
        const bValue = b[key];

        if (aValue === undefined || bValue === undefined) return 0;

        // richTextMapはソート対象外
        if (key === "richTextMap") return 0;

        // 日付でソート
        if (key === "submittedAt") {
          return asc
            ? new Date(aValue as string | Date).getTime() - new Date(bValue as string | Date).getTime()
            : new Date(bValue as string | Date).getTime() - new Date(aValue as string | Date).getTime();
        }

        // numberをnumber型に変換してソート
        if (!isNaN(Number(aValue)) && !isNaN(Number(bValue))) {
          const aNum = Number(aValue);
          const bNum = Number(bValue);
          return asc ? aNum - bNum : bNum - aNum;
        }

        //デフォルト的にstringで比較する
        return asc
          ? String(aValue).localeCompare(String(bValue))
          : String(bValue).localeCompare(String(aValue));
      });
    }

    // ステータスでフィルタリング
    if (statusFilter !== "all") {
      result = result.filter((row) => row.status === statusFilter);
    }

    setPage(1);
    return result;
  }, [searchTerm, data, sortConfig, statusFilter]);

  const paginatedData = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-40">
        <CircularProgress />
      </div>
    );
  }

  // 行選択処理
  const handleRowClick = (record: ApplicationEntriesType) => {
    // 選択した行の申請番号を設定し、ダイアログを閉じる
    setSelectDialogValue(record.number);
    handleCloseDialog();
  };

  // フィルターの条件を初期化する
  const filterReset = () => {
    // sessionStorageを削除する
    sessionStorage.removeItem("searchText_ApplicationAudit");
    sessionStorage.removeItem("showItems_ApplicationAudit");
    sessionStorage.removeItem("visibleColomns_ApplicationAudit");
    sessionStorage.removeItem("paging_ApplicationAudit");

    // フィルター条件の初期化
    setSearchTerm("");
    setVisibleColumns(new Set(Object.keys(columnLabels)));
    setRowsPerPage(defaultRowPerPage);
    setPage(1);
    setStatusFilter("Completed");
  };

  // テキストボックスのテキストをクリア
  const handleFilterTextClear = () => {
    // フィルター検索のテキストを初期化
    setSearchTerm("");
    // sessionStorageの値をクリアする
    sessionStorage.removeItem("searchText_ApplicationAudit");
  };

  return (
    <div className="p-4">
      {/* 行1: 検索欄 */}
      <Box sx={{ mx: 1, mt: 1 }}>
        <TextField
          label="検索"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => {
            const value = e.target.value;
            setSearchTerm(value);
            // 空の場合はsessionStorageを削除。それ以外は値を格納する。
            if (value === "") {
              sessionStorage.removeItem("searchText_ApplicationAudit");
            } else {
              sessionStorage.setItem("searchText_ApplicationAudit", value);
            }
          }}
          sx={{ width: 220 }}
          slotProps={{
            input: {
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleFilterTextClear}
                    edge="end"
                    sx={{
                      backgroundColor: "transparent",
                      "&:hover": { backgroundColor: "transparent" },
                    }}
                  >
                    <ClearSharpIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>
      {/* 行2: ステータスフィルター + リセット */}
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, mx: 1, mt: 2 }}>
        <Typography variant="subtitle1">ステータス:</Typography>
        {statusFilterLabels.map(({ key, label }) => (
          <Chip
            key={key}
            label={label}
            clickable
            onClick={() => setStatusFilter(key)}
            sx={{
              backgroundColor: statusFilter === key ? theme.palette.custom.blue : "grey.200",
              color: "text.primary",
              "&:hover": {
                backgroundColor: statusFilter === key ? "primary.dark" : "grey.400",
              },
            }}
          />
        ))}
        {/* フィルターのリセット機能 */}
        <Chip
          label="リセット"
          onClick={filterReset}
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            "&:hover": { backgroundColor: theme.palette.primary.dark },
            ml:2
          }}
        />
      </Box>
      {/* 表示件数・表示項目設定 */}
      <div>
        <Button
          sx={{ ml: 1, mt: 1, typography: "buttonMedium" }}
          onClick={() => setShowColumnSettings(!showColumnSettings)}
          startIcon={showColumnSettings ? <ChevronUp /> : <ChevronDown />}
        >
          表示設定
        </Button>
      </div>
      <Box sx={{ pl: 2, pb: 1 }}>
        {showColumnSettings && (
          <div>
            <div>表示件数:</div>
            <RadioGroup
              row
              value={rowsPerPage.toString()}
              onChange={(e) => {
                setRowsPerPage(parseInt(e.target.value));
                sessionStorage.setItem(
                  "showItems_ApplicationAudit",
                  e.target.value
                ); // 表示設定が変わったら、sessionStorageに値を格納する。
                setPage(1);
                sessionStorage.removeItem("paging_ApplicationAudit"); // 表示設定が変わったら、sessionStorageの値を削除する。
              }}
            >
              {rowPerPageList.map((num) => (
                <FormControlLabel
                  key={num}
                  value={num.toString()}
                  control={
                    <Radio
                      sx={{
                        color: "primary",
                        "&.Mui-checked": {},
                        transform: "scale(0.6)",
                      }}
                    />
                  }
                  label={`${num}件`}
                />
              ))}
            </RadioGroup>
            <div>
              <div className="font-semibold mb-2">表示項目:</div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(columnLabels).map(([key, label]) => (
                  <label key={key} className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={visibleColumns.has(key)}
                      onChange={() => toggleColumn(key)}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}
      </Box>

      {/* ページング */}
      <Box
        sx={{
          ml: 2,
          my: 1,
          display: "flex",
          gap: 2,
          justifyContent: "flex-start",
          alignItems: "center",
        }}
      >
        {/* 件数表示 */}
        <div style={{ marginBottom: 1, color: "#666", fontSize: "1rem" }}>
          全 {filteredData.length} 件中{" "}
          {filteredData.length === 0
            ? "0 件 〜 0 件"
            : `${(page - 1) * rowsPerPage + 1} 件 〜 ${Math.min(
                page * rowsPerPage,
                filteredData.length
              )} 件`}{" "}
          を表示
        </div>
        <div>
          <Pagination
            count={Math.ceil(filteredData.length / rowsPerPage)}
            page={page}
            onChange={(e, value) => {
              setPage(value);
              sessionStorage.setItem(
                "paging_ApplicationAudit",
                value.toString()
              ); // ページが変わったら、現在のページをsessionStorageに格納する。
            }}
            color="primary"
            sx={{
              "& .MuiPaginationItem-root": {
                minWidth: "20px",
                height: "20px",
                padding: "1px",
              },
            }}
          />
        </div>
      </Box>

      {/* テーブル */}
      <TableContainer
        component={Paper}
        sx={{
          ml: isMobile ? 0 : 1,
          mb: 2,
          overflowX: "auto", // 横方向でスクロールを表示
          maxWidth: "200%",
        }}
      >
        <Table
          sx={{ tableLayout: "auto", minWidth: 2000 }}
          aria-label="customized table"
        >
          <TableHead>
            <TableRow>
              {Object.entries(columnLabels).map(([key, label]) => {
                if (!visibleColumns.has(key)) return null;
                const isActive = sortConfig?.key === key;
                const asc = sortConfig?.asc;

                return (
                  <StyledTableCell
                    key={key}
                    onClick={() => {
                      setPage(1); // 第一ページに戻る
                      setSortConfig((prev) =>
                        prev?.key === key
                          ? {
                              key: key as keyof ApplicationEntriesType,
                              asc: !prev.asc,
                            }
                          : {
                            key: key as keyof ApplicationEntriesType,
                              asc: true,
                            }
                      );
                    }}
                    onMouseEnter={() => setHoveredColumn(key)}
                    onMouseLeave={() => setHoveredColumn(null)}
                    style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                  >
                    <div
                      style={{ display: "inline-flex", alignItems: "center" }}
                    >
                      {label}
                      {(isActive || hoveredColumn === key) &&
                        (asc && isActive ? (
                          <ChevronUp
                            fontSize="small"
                            className="ml-1 text-gray-500"
                          />
                        ) : !asc && isActive ? (
                          <ChevronDown
                            fontSize="small"
                            className="ml-1 text-gray-500"
                          />
                        ) : (
                          <ChevronDown
                            fontSize="small"
                            className="ml-1 text-gray-300"
                          />
                        ))}
                    </div>
                  </StyledTableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row) => (
              <StyledTableRow
                key={row.applicationEntryId}
                onClick={(event) => {
                  event.stopPropagation(); // クリックエリアでイベントバブリング停止
                  handleRowClick(row);
                }}
                sx={{
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "#e3f2fd", // 浅いブルー
                    boxShadow: "inset 0 0 0 1px #90caf9",
                  },
                }}
              >
                {Object.entries(row)
                  .filter(
                    ([key]) => visibleColumns.has(key) && key in columnLabels
                  )
                  .map(([key, value]) => {
                    if (key === "formSummary") {
                      // richTextMapがある場合は、formSummaryを処理
                      const richTextElements: React.ReactNode[] = [];

                      if (row.richTextMap) {
                        // formSummaryを行ごとに分割
                        const lines = value.split('\n');
                        lines.forEach((line: string, index: number) => {
                          // "label: [リッチテキスト]" の形式を検出
                          const match = line.match(/^(.+?):\s*\[リッチテキスト\]$/);
                          if (match) {
                            const label = match[1];
                            const htmlContent = row.richTextMap![label];
                            if (htmlContent) {
                              // labelを表示（太字なし）
                              richTextElements.push(
                                <div key={`label-${index}`}>
                                  {label}:
                                </div>
                              );
                              // HTMLをパースして表示
                              richTextElements.push(
                                <div key={`content-${index}`} style={{ marginLeft: '8px', marginBottom: '8px' }}>
                                  {parseRichTextHTML(htmlContent)}
                                </div>
                              );
                            }
                          } else {
                            // 通常の行
                            richTextElements.push(
                              <div key={`line-${index}`}>{line}</div>
                            );
                          }
                        });
                      }

                      return (
                        <StyledTableCell key={key}>
                          {row.richTextMap ? (
                            <Box
                              onClick={(e) => {
                                e.stopPropagation(); // イベントバブリングを停止
                              }}
                              onMouseDown={(e) => {
                                e.stopPropagation(); // リサイズ時のイベントバブリングを停止
                              }}
                              component="div"
                              sx={{
                                fontFamily: theme.typography.fontFamily,
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                padding: "2px",
                                minWidth: "250px",
                                height: "40px",
                                resize: "both",
                                overflow: "auto",
                                backgroundColor: "#f9f9f9",
                                width: "60%",
                                boxSizing: "border-box",
                                cursor: "default",
                              }}
                            >
                              {richTextElements}
                            </Box>
                          ) : (
                            <textarea
                              value={value}
                              readOnly
                              onClick={(e) => {
                                e.stopPropagation(); // イベントバブリングを停止
                              }}
                              onMouseDown={(e) => {
                                e.stopPropagation(); // リサイズ時のイベントバブリングを停止
                              }}
                              style={{
                                fontFamily: theme.typography.fontFamily,
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                padding: "2px",
                                minWidth: "250px",
                                height: "40px",
                                resize: "both",
                                overflow: "auto",
                                backgroundColor: "#f9f9f9",
                                width: "60%",
                                boxSizing: "border-box",
                                cursor: "default",
                              }}
                            />
                          )}
                        </StyledTableCell>
                      );
                    }

                    return (
                      <StyledTableCell key={key} align="left">
                        {key === "status" ? (statusMap[value] ?? value) : key === "submittedAt" ? (dayjs(value).isValid() ? dayjs(value).format("YYYY/MM/DD") : "未提出") : value}
                      </StyledTableCell>
                    );
                  })}
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {/* ページング */}
      <Box
        sx={{
          ml: 2,
          display: "flex",
          gap: 2,
          justifyContent: "flex-start",
          alignItems: "center",
        }}
      >
        {/* 件数表示 */}
        <div style={{ marginTop: 1, color: "#666", fontSize: "1rem" }}>
          全 {filteredData.length} 件中{" "}
          {filteredData.length === 0
            ? "0 件 〜 0 件"
            : `${(page - 1) * rowsPerPage + 1} 件 〜 ${Math.min(
                page * rowsPerPage,
                filteredData.length
              )} 件`}{" "}
          を表示
        </div>
        <div>
          <Pagination
            count={Math.ceil(filteredData.length / rowsPerPage)}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
            sx={{
              "& .MuiPaginationItem-root": {
                minWidth: "20px",
                height: "20px",
                padding: "1px",
              },
            }}
          />
        </div>
      </Box>
    </div>
  );
};
// #endregion

    return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={numbers}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            onChange={(e, newValue) => handleChange(e,newValue)}
            onClose={handleClose}
            renderTags={(value: string[], getTagProps) =>
                value.map((option: string, index: number) => (
                    <Chip
                        variant="outlined"
                        clickable
                        label={
                            <span style={{ color: "#1976d2", textDecoration: "underline" }}>
                                {option}
                            </span>
                        }
                        onClick={() => handleClick(option)}
                        {...getTagProps({ index })}
                    />
                ))
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="outlined"
                    label="申請番号"
                    placeholder="Enter申請番号"
                    required={required}
                    error={error}
                    helperText={helperText}
                />
            )}
            sx={{ flexGrow: 1 }}
        />
        {/* 補助アイコン */}
      <IconButton onClick={handleOpenDialog} sx={{ ml: 1 }}>
        <SearchIcon />
      </IconButton>

      {/* 子画面（ダイアログ） */}
      <Dialog open={openDialog} onClose={handleCloseDialog}
        PaperProps={{
            sx: {
                width: '75%',
                maxWidth : '100%' , 
                margin: 'auto',
            }
        }} fullWidth>
        <DialogTitle>入力補助</DialogTitle>
        <DialogContent>
          <ApplicationList/>
        </DialogContent>
      </Dialog>
        </Box>
    );
};

export default ApplicationNumberField;
